
import React, { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Hero from '../../components/feature/Hero';
import Problems from './components/Problems';
import PropertyTypes from './components/PropertyTypes';
import BeforeAfter from './components/BeforeAfter';
import WhyLawyer from './components/WhyLawyer';
import Process from './components/Process';
import Pricing from './components/Pricing';
import Introduction from './components/Introduction';
import Office from './components/Office';
import FinalCTA from './components/FinalCTA';
import StructuredData from '../../components/feature/StructuredData';

const Home = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://hudosan-shobun.com";

  useEffect(() => {
    document.title = '愛知県負動産処分｜山林・農地・空き家の処分なら冨田・島岡法律事務所';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '愛知県で山林・農地・空き家など売れない不動産（負動産）の処分にお困りですか？相続土地国庫帰属制度の申請、共有名義の解消、農地転用まで弁護士が対応。初回相談無料。名古屋駅から徒歩圏内。');
    }
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LegalService",
    "name": "冨田・島岡法律事務所",
    "description": "愛知県で負動産処分を専門とする法律事務所。相続した価値のない不動産（山林・農地・空き家）の処分を法的手続きで確実に解決。",
    "url": siteUrl,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "栄2-2-17 名古屋情報センタービル6階",
      "addressLocality": "名古屋市中区",
      "addressRegion": "愛知県",
      "postalCode": "460-0008",
      "addressCountry": "JP"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "35.1667",
      "longitude": "136.9000"
    },
    "areaServed": [
      { "@type": "State", "name": "愛知県" },
      { "@type": "State", "name": "岐阜県" },
      { "@type": "State", "name": "三重県" },
      { "@type": "State", "name": "静岡県" }
    ],
    "serviceType": ["負動産処分", "相続不動産処分", "山林処分", "農地処分", "空き家処分", "相続土地国庫帰属制度"],
    "priceRange": "$$",
    "telephone": "052-588-0874",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "法律サービス",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "初回法律相談",
            "description": "負動産処分に関する初回相談は無料。対面またはWeb会議で実施。"
          },
          "price": "0",
          "priceCurrency": "JPY"
        },
        {
          "@type": "Offer", 
          "itemOffered": {
            "@type": "Service",
            "name": "負動産処分手続き",
            "description": "相続した価値のない不動産の法的処分手続きを代行。成功報酬110万円。"
          },
          "price": "1100000",
          "priceCurrency": "JPY"
        }
      ]
    }
  };

  const localBusinessData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "冨田・島岡法律事務所｜負動産処分専門",
    "image": siteUrl + "/og-image.jpg",
    "url": siteUrl,
    "telephone": "052-588-0874",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "栄2-2-17 名古屋情報センタービル6階",
      "addressLocality": "名古屋市中区",
      "addressRegion": "愛知県",
      "postalCode": "460-0008",
      "addressCountry": "JP"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "35.1667",
      "longitude": "136.9000"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      }
    ],
    "priceRange": "$$"
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "負動産とは何ですか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "負動産とは、維持費や税金などのコストが収益を上回る不動産のことです。山林、農地、空き家などが該当し、相続しても経済的負担となる不動産を指します。愛知県内でも多くの方がこのような不動産の処分にお困りです。"
        }
      },
      {
        "@type": "Question",
        "name": "相続した不動産を処分するにはどうすればよいですか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "相続した不動産の処分には、売却、寄付、相続放棄、相続土地国庫帰属制度などの方法があります。それぞれに法的手続きが必要で、専門的な知識が求められるため、弁護士に相談することをお勧めします。"
        }
      },
      {
        "@type": "Question",
        "name": "初回相談は無料ですか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "はい、負動産処分に関する初回法律相談は無料です。対面またはWeb会議での相談が可能で、事前予約制となっております。"
        }
      },
      {
        "@type": "Question",
        "name": "どのような地域に対応していますか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "主に愛知県を中心に、岐阜県、三重県、静岡県の案件に対応しております。遠方の方もWeb会議での相談が可能です。"
        }
      },
      {
        "@type": "Question",
        "name": "処分にかかる期間はどのくらいですか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "不動産の種類や状況により異なりますが、一般的に3ヶ月から1年程度かかります。山林や農地の場合は手続きが複雑になることが多く、より時間を要する場合があります。"
        }
      }
    ]
  };

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "ホーム",
        "item": siteUrl
      }
    ]
  };

  const webPageData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "負動産処分の弁護士｜愛知県名古屋市｜冨田・島岡法律事務所",
    "description": "愛知県で負動産処分を専門とする法律事務所。相続した山林・農地・空き家の処分を弁護士がサポート。初回相談無料。",
    "url": siteUrl,
    "isPartOf": {
      "@type": "WebSite",
      "name": "冨田・島岡法律事務所",
      "url": siteUrl
    },
    "about": {
      "@type": "Thing",
      "name": "負動産処分"
    },
    "mentions": [
      { "@type": "Thing", "name": "相続土地国庫帰属制度" },
      { "@type": "Thing", "name": "山林処分" },
      { "@type": "Thing", "name": "農地処分" },
      { "@type": "Thing", "name": "空き家処分" }
    ]
  };

  return (
    <div className="min-h-screen">
      <StructuredData data={structuredData} />
      <StructuredData data={localBusinessData} />
      <StructuredData data={faqStructuredData} />
      <StructuredData data={breadcrumbData} />
      <StructuredData data={webPageData} />
      
      <Header />
      <Hero />
      <Problems />
      <PropertyTypes />
      <BeforeAfter />
      <WhyLawyer />
      <Process />
      <Pricing />
      <Introduction />
      <Office />
      <FinalCTA />
    </div>
  );
};

export default Home;
